import api from "../utils/api";

/**
 * Efficiency Initiatives API client — Tools -> Efficiency Initiatives.
 *
 * Backed by routers/efficiency_initiatives.py in the enviz_api repo. Unlike
 * ticketService.js there is no localStorage fallback: an initiative is the record
 * the savings numbers are computed from, so a row that only exists in one browser
 * would report impact nobody else can see. A failed write surfaces as an error.
 */

const BASE = "/efficiency-initiatives";

/** Pull the readable message out of the backend's {error:{message,reason}} envelope. */
export function initiativeErrorMessage(err, fallback = "Something went wrong.") {
  return (
    err?.response?.data?.error?.message ||
    err?.response?.data?.detail ||
    err?.parsedError?.message ||
    err?.message ||
    fallback
  );
}

/**
 * Form dropdown options: statuses, node types, normalization variables and the
 * full insight catalogue grouped by category.
 *
 * Fetched rather than hardcoded — the catalogue is derived on the server from the
 * enSight *_META dicts, so a newly added insight appears here with no UI change.
 */
export async function getInitiativeOptions() {
  const { data } = await api.get(`${BASE}/options`);
  return data;
}

/** Building / Section / Machine nodes this user is allowed to target. */
export async function getSelectableNodes(username) {
  const { data } = await api.get(`${BASE}/nodes`, { params: { username } });
  return data.nodes || [];
}

/**
 * List initiatives with the tab's filters applied.
 *
 * Empty strings and "All" are stripped rather than sent: the backend treats a
 * missing param as "no filter", and sending status="" would fail validation.
 */
export async function listInitiatives(username, filters = {}) {
  const params = { username };
  const passthrough = ["status", "node_type", "meter_id", "insight_key", "q", "from", "to"];
  passthrough.forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== "" && value !== "All") {
      params[key] = value;
    }
  });
  if (filters.offset != null) params.offset = filters.offset;
  if (filters.limit != null) params.limit = filters.limit;

  const { data } = await api.get(BASE, { params });
  return data;
}

/** One initiative, including its change history. */
export async function getInitiative(initiativeId, username) {
  const { data } = await api.get(`${BASE}/${initiativeId}`, { params: { username } });
  return data.data;
}

export async function createInitiative(payload) {
  const { data } = await api.post(BASE, payload);
  return data.data;
}

/** Full replace — the edit form submits every field, so a cleared date clears it. */
export async function updateInitiative(initiativeId, payload) {
  const { data } = await api.put(`${BASE}/${initiativeId}`, payload);
  return data.data;
}

/** Status transition. Also the "Complete" row action. */
export async function setInitiativeStatus(initiativeId, payload) {
  const { data } = await api.patch(`${BASE}/${initiativeId}/status`, payload);
  return data.data;
}

/** Soft delete: the row stops appearing but historical totals stay reproducible. */
export async function deleteInitiative(initiativeId, username) {
  const { data } = await api.delete(`${BASE}/${initiativeId}`, { params: { username } });
  return data;
}

/**
 * Measured before/after impact for one initiative.
 *
 * Always recomputed server-side from meter data, so this is a live call rather
 * than something to cache alongside the initiative row. `grain` buckets the trend
 * series: daily | weekly | monthly | yearly.
 */
export async function getInitiativeImpact(initiativeId, username, grain = "daily") {
  const { data } = await api.get(`${BASE}/${initiativeId}/impact`, {
    params: { username, grain },
  });
  return data.impact;
}

/**
 * Ranked initiative performance, plus the best and worst outliers.
 *
 * `rankBy`: energy | financial | payback | loss. "loss" is accepted but the
 * response reports it unavailable until the insight-loss comparison exists —
 * check `ranking_available` before rendering an order.
 */
export async function getInitiativePerformance(
  username,
  { rankBy = "energy", status, top = 5 } = {}
) {
  const params = { username, rank_by: rankBy, top };
  if (status && status !== "All") params.status = status;
  const { data } = await api.get(`${BASE}/performance`, { params });
  return data;
}

/**
 * Portfolio-wide totals, ROI, payback and the cumulative benefit curve.
 *
 * Returns CO2 and insight-loss totals as unavailable rather than zero — check
 * `.available` on each before rendering a number.
 */
export async function getOverallImpact(username, status) {
  const params = { username };
  if (status && status !== "All") params.status = status;
  const { data } = await api.get(`${BASE}/overall-impact`, { params });
  return data;
}
